<template>
    <cs-card>
        <template #header>
            <div class="cs-card__icon">
                <mdi-icon icon="mdiFileExcelOutline" />
            </div>

            <div class="cs-card__title">
                Export Prices
            </div>
        </template>

        <div>
            <div>
                Download an Excel spreadsheet of all current prices.
            </div>

            <div class="mt-4">
                <lsn-input
                    v-model="date"
                    class="lsn-form__wrapper--padless mb-4"
                    label="Date"
                    ckey="date"
                    :placeholder="DATE_FORMAT"
                    :error="dateError"
                />
            </div>

            <div
                class="cursor-pointer flex items-center gap-1 w-fit select-none"
                @click="includeNonSl = !includeNonSl"
            >
                <mdi-icon
                    :icon="includeNonSl ? 'mdiCheck' : 'mdiCheckboxBlankOutline'"
                    class="w-5 h-5"
                    :class="{ 'text-primary-500': includeNonSl }"
                />

                <div class="text-sm">
                    Include Non-SL
                </div>
            </div>

            <div class="flex justify-end mt-8">
                <div
                    tabindex="0"
                    class="lsn-btn lsn-btn--primary-outline"
                    :class="{ 'lsn-btn--disabled': loading || !!dateError }"
                    @click="download"
                >
                    <template v-if="loading">
                        Getting Prices…
                    </template>

                    <template v-else>
                        Get Prices
                    </template>
                </div>
            </div>
        </div>
    </cs-card>
</template>

<script setup>
import { computed, ref } from 'vue';
import { format, parse, isValid } from 'date-fns';
import axios  from '@/axios';
import CsCard from '@/components/CsCard.vue';

const DATE_FORMAT = 'yyyy-MM-dd';

const loading = ref(false);
const date = ref(format(new Date(), DATE_FORMAT));
const includeNonSl = ref(false);

const dateError = computed(() =>
{
    try
    {
        const d = parse(date.value, DATE_FORMAT, new Date());
        if(!isValid(d))
        {
            throw new Error(`"${date.value}" is invalid. Format: ${DATE_FORMAT}`);
        }
    }
    catch(e)
    {
        return e.message;
    }

    return null;
});

const filename = computed(() =>
{
    return includeNonSl.value
        ? `Prices Export ${date.value} Non-SL.xlsx`
        : `Prices Export ${date.value}.xlsx`;
});

async function download()
{
    if(loading.value)
    {
        return;
    }

    if(dateError.value !== null)
    {
        return;
    }

    loading.value = true;

    try
    {
        let url = `/api/article-price/export/${date.value}`;
        if(includeNonSl.value)
        {
            url += '?include-non-sl';
        }

        const { data } = await axios.get(url, { timeout: 15_000 });
        // Use an in-memory <a> tag to download the base64-encoded Excel file
        const a = document.createElement('a');
        a.href = `data:application/octet-stream;base64,${data}`;
        a.target = '_blank';
        a.download = filename.value;
        a.click();
    }
    catch(error)
    {
        // todo: handle error
    }
    finally
    {
        loading.value = false;
    }
}
</script>
